import type { Locale } from "./locale.js";

/**
 * Dates and times, in the language the interface is in.
 *
 * Every screen used to build its own `new Intl.DateTimeFormat("ru-RU", …)` at
 * module scope, which is fast and was fine while there was one language. It
 * stops working the moment the language can change after the module loaded, so
 * the formatters move here and are asked for by name.
 *
 * **English means `en-GB`, on purpose.** The interface language is a choice
 * about words; it is not a request to reorder every date. `en-US` would give
 * "Aug 25" and "7:00 PM" where the whole app — the calendar grid, the agenda,
 * the chat separators — is laid out for "25 Aug" and "19:00". British English
 * keeps the day first and the clock at 24 hours, so switching language changes
 * the words and nothing else. If that ever needs to be a separate setting, this
 * is the one line to change.
 */
const TAGS: Record<Locale, string> = {
  ru: "ru-RU",
  en: "en-GB",
};

/** The shapes the screens actually ask for. Adding one here beats inlining it. */
const OPTIONS = {
  time: { hour: "2-digit", minute: "2-digit" },
  dayMonthLong: { day: "numeric", month: "long" },
  dayMonthShort: { day: "numeric", month: "short" },
  dayMonthYearLong: { day: "numeric", month: "long", year: "numeric" },
  dayMonthYearShort: { day: "numeric", month: "short", year: "numeric" },
  weekdayLong: { weekday: "long" },
  weekdayShort: { weekday: "short" },
  monthName: { month: "long" },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type FormatName = keyof typeof OPTIONS;

/**
 * Building an `Intl.DateTimeFormat` is expensive enough that a chat screen
 * doing it per message is measurable, so each one is built once per language
 * and kept.
 */
const cache = new Map<string, Intl.DateTimeFormat>();

export function dateFormat(locale: Locale, name: FormatName): Intl.DateTimeFormat {
  const key = `${locale}:${name}`;
  let format = cache.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(TAGS[locale], OPTIONS[name]);
    cache.set(key, format);
  }
  return format;
}

/** The eight formatters as one object, for a screen that wants several. */
export type DateFormats = Record<FormatName, Intl.DateTimeFormat>;

const bundles = new Map<Locale, DateFormats>();

export function dateFormats(locale: Locale): DateFormats {
  let bundle = bundles.get(locale);
  if (!bundle) {
    bundle = Object.fromEntries(
      (Object.keys(OPTIONS) as FormatName[]).map((name) => [name, dateFormat(locale, name)]),
    ) as DateFormats;
    bundles.set(locale, bundle);
  }
  return bundle;
}

/**
 * Пн … вс, or Mon … Sun.
 *
 * Derived from a week that is known to start on a Monday (1 January 2024) rather
 * than written out, so the names come from Intl in whichever language is on.
 * Monday-first is deliberate and not locale-derived: the calendar grid is built
 * Monday-first for both languages, and a week that silently starts on Sunday in
 * English would put every date in the wrong column.
 */
export function weekdayNames(locale: Locale): string[] {
  const format = dateFormat(locale, "weekdayShort");
  return Array.from({ length: 7 }, (_, i) => format.format(new Date(2024, 0, 1 + i)));
}

/** Sorting and case-folding follow the language too — `ё` belongs next to `е`. */
export function collationTag(locale: Locale): string {
  return TAGS[locale];
}
